import { useLazyQuery } from '@apollo/client';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Button, Headline2, IconButton, Meta, theme } from 'aimo-ui';
import { MenuItemWrapped } from 'components/account/menuItems/MenuComponents';
import TextWithLinks from 'components/common/TextWithLinks';
import Icon from 'components/icons/Icon';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import HslContext from 'context/HslContext';
import ServiceContext from 'context/ServiceContext';
import { AUTHENTICATE_DEVICE_TIME } from 'graphql/queries/authenticateDeviceTime';
import useAfterAuthenticationRedirect from 'hooks/useAfterAuthenticationRedirect';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import { getLanguage } from 'i18n/init';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, ScrollView, Text } from 'react-native';
import styled from 'styled-components/native';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';
import { FooterWrapper, MainWrapper } from 'styles/ModalStackContainerStyles';
import {
  Lang,
  Query,
  QueryAuthenticateDeviceTimeArgs,
} from 'types/generatedSchemaTypes';
import { HslParkingStackParamList } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import {
  Language,
  ParkAndRideDynamicInfo,
  parseFeatureFlagObject,
} from 'utils/featureFlagUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { Row } from '../styles/styles';
import HslTicketList from './HslTicketList';
import HslWizardSheet from './HslWizardSheet';
import InfoCardComponent from './InfoCardComponent';
type ParkAndRideTutorialViewProps = {
  mockDateTime?: Date;
};

const Container = styled.View`
  flex: 1;
  background-color: ${(p) => p.theme.colors.gray100};
`;

const InfoCard = styled.View`
  background-color: ${(p) => p.theme.colors.white};
  padding: 16px;
  margin: 20px;
  border-radius: 10px;
  shadow-color: #000;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 3;
`;

const HslTicketInfoView: React.FC<ParkAndRideTutorialViewProps> = ({
  mockDateTime,
}) => {
  const { t } = useTranslation();
  const { setCurrentScreen, currentScreen, hasHslAuthentication, setHslToken } =
    useContext(HslContext);
  const {
    flags: { park_and_ride_content },
  } = useContext(FeatureFlagsContext);
  const languageDynamicInfo = parseFeatureFlagObject<ParkAndRideDynamicInfo>(
    park_and_ride_content,
    'park_and_ride_content'
  );

  const lang = (getLanguage() as Lang).toLocaleUpperCase() as Language;
  const termsUrl = languageDynamicInfo[lang].termsUrl;
  const privacyUrl = languageDynamicInfo[lang].privacyUrl;
  const customerUrl = languageDynamicInfo[lang].customerUrl;
  const { myHslTickets } = useContext(ServiceContext);
  const { isLoggedIn } = useIsLoggedIn();
  const { navigateToAccountLogin } = useAfterAuthenticationRedirect();
  const { goBack } =
    useNavigation<StackNavigationProp<HslParkingStackParamList>>();
  const { params } = useRoute<RouteProp<HslParkingStackParamList, 'info'>>();

  const [authenticateDeviceTime, { loading }] = useLazyQuery<
    Query,
    QueryAuthenticateDeviceTimeArgs
  >(AUTHENTICATE_DEVICE_TIME);

  const parkAndRideSteps = [
    {
      icon: 'Services-Outlined',
      text: t('hslTicket.parkAndRideStep1'),
    },
    {
      icon: 'Park-Outlined',
      text: t('hslTicket.parkAndRideStep2'),
      linkText: t('hslTicket.selectedFacilities'),
    },
    {
      icon: 'Time-Outlined',
      text: t('hslTicket.parkAndRideStep3'),
    },
    {
      icon: 'Star-Outlined',
      text: t('hslTicket.parkAndRideStep4'),
    },
  ];

  const onBuyClick = async () => {
    const { data, error } = await authenticateDeviceTime({
      variables: {
        input: {
          deviceTime: mockDateTime?.toISOString() || new Date().toISOString(),
        },
      },
    });

    if (error || !data?.authenticateDeviceTime?.valid) {
      return setCurrentScreen('INVALID_TIME');
    }
    if (!isLoggedIn) {
      return navigateToAccountLogin();
    }
    return setCurrentScreen('SELECT_TICKET');
  };

  //After returning from HSL authentication redirect back to authentication screen
  useEffect(() => {
    if (params?.token && !hasHslAuthentication()) {
      setHslToken(params.token);
      setCurrentScreen('AUTHENTICATE');
    }
  }, [params, hasHslAuthentication, setCurrentScreen, setHslToken]);

  return (
    <>
      <Container testID="hsl-ticket-info">
        {!isWeb && (
          <ColumnWithMargins mt={45} ml={20}>
            <Row>
              <IconButton
                rounded={true}
                fullWidth={false}
                buttonType="secondary"
                {...testIdentifiers('hsl-info-backButton')}
                onPress={() => goBack()}
                iconComponent={() => (
                  <Icon name="back" color={theme.colors.white} />
                )}
                accessibilityLabel={t('goBack')}
              />
              <ColumnWithMargins ml={10}>
                <Headline2>
                  {myHslTickets?.length > 0
                    ? t('hslTicket.title')
                    : t('hslTicket.parkRide')}
                </Headline2>
              </ColumnWithMargins>
            </Row>
          </ColumnWithMargins>
        )}
        <ScrollView>
          {myHslTickets?.length === 0 && (
            <StyledWrapper
              mb={0}
              flex={1}
              alignItems="center"
              justifyContent="center">
              <InfoCardComponent parkAndRideSteps={parkAndRideSteps} />
              <StyledWrapper
                center
                alignItems="center"
                justifyContent="center"
                flexGrow={10}>
                <Meta testID="hsl-no-tickets">{t('hslTicket.noTickets')}</Meta>
                <Meta testID="hsl-no-tickets">
                  {t('hslTicket.noTicketsDesc')}
                </Meta>
              </StyledWrapper>
            </StyledWrapper>
          )}
          {myHslTickets?.length > 0 && (
            <MainWrapper vAlignCenter={true}>
              {myHslTickets?.length > 0 && (
                <HslTicketList tickets={myHslTickets} />
              )}
            </MainWrapper>
          )}
        </ScrollView>
        <FooterWrapper>
          <StyledWrapper maxWidth={350} center mb={0} mt={0}>
            <MenuItemWrapped
              testID="hsl-customer-support"
              menuLabel={t('hslTicket.customerSupportLink')}
              onPress={() => Linking.openURL(customerUrl)}
              color={theme.colors.black}
              borderColor={theme.colors.gray100}
              noBorder
              withArrow
              arrowSize="s"
              divider
              paddingVertical={1}
            />
          </StyledWrapper>
          <StyledWrapper maxWidth={350} mt={20} center alignItems="center">
            <Text style={{ textAlign: 'center' }}>
              <TextWithLinks
                i18nKey="hslTicket.privacyPolicyInfo"
                linkColor={theme.colors.black}
                links={{
                  terms: { onPress: () => Linking.openURL(termsUrl) },
                  privacy: { onPress: () => Linking.openURL(privacyUrl) },
                }}
              />
            </Text>
          </StyledWrapper>
          <StyledWrapper maxWidth={350} center mb={20}>
            <Button
              testID="hsl-buy-ticket"
              text={t('hslTicket.buy')}
              size="l"
              loading={loading}
              onPress={onBuyClick}
            />
          </StyledWrapper>
        </FooterWrapper>
      </Container>
      {currentScreen !== '' && <HslWizardSheet />}
    </>
  );
};

export default HslTicketInfoView;
